import { ChangeElement, List } from "hooks/useHandleObjList"
import BodyText from "components/Typography/BodyText"
import Button from "components/EvtKit/button/Button"
import { ButtonWithConfirmation } from "components/ButtonWithConfirmation"
import Caption from "components/Typography/Caption"
import IconAddLarge from "components/EvtKit/icons/IconAddLarge"
import IconDeleteSmall from "components/EvtKit/icons/IconDeleteSmall"
import IconInformativeYellow from "components/EvtKit/icons/IconInformativeYellow"
import Input from "components/EvtKit/input/Input"

import clsx from "clsx"
import { memo, useCallback, useState } from "react"
import styled from "styled-components"
import { useNavigate } from "react-router-dom"
import { productsBenefitsLimit } from "pages/products/constants/defaultValues"
import { LIMIT_BENEFITS_CHARACTERS } from "constants/product"

const BenefitsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
`

const InstructionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const InstructionsListContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 32px;
`

const InstructionsList = styled.ul`
  display: flex;
  flex-direction: column;
  width: 100%;
  list-style-type: disc;
  color: ${(props) => props.theme.palette.informative};
`

const AddBenefitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background-color: transparent;
  cursor: pointer;

  &.disabled {
    cursor: default;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  padding: 16px 0px 32px 0px;
`

const BenefitListContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-top: 16px;
`

const LineContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  width: 100%;
  gap: 12px;
  align-items: center;
`

const ContainerBenefitsInput = styled.div`
  width: 100%;
`

const IconContainerDelete = styled.span`
  display: flex;
  justify-content: end;
  cursor: pointer;
`

const benefitsLimit = productsBenefitsLimit

interface IBenefitItem {
  value: string
}

interface BenefitsProps {
  benefitsList: List<IBenefitItem>
  addBenefit: (obj?: IBenefitItem) => void
  changeBenefit: ChangeElement<IBenefitItem>
  removeBenefit: (key: string) => void
  disabled: boolean
  onSubmit: () => void
  isLoading: boolean
}

const ProductsBenefits = (props: BenefitsProps) => {
  const { benefitsList, addBenefit, changeBenefit, removeBenefit, disabled, onSubmit, isLoading } = props

  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  const handleAddNewBenefit = useCallback(() => {
    if (benefitsList.length < benefitsLimit) {
      addBenefit()
    }
  }, [benefitsList, addBenefit])

  const handleBenefitChange = useCallback(
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      changeBenefit(key, "value", e.target.value)

      if (e.target.value.length > LIMIT_BENEFITS_CHARACTERS) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [key]: `O benefício deve ter no máximo ${LIMIT_BENEFITS_CHARACTERS} caracteres. Você inseriu ${e.target.value.length} caracteres.`
        }))
      } else {
        setErrors((prevErrors) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [key]: _, ...rest } = prevErrors

          return rest
        })
      }
    },
    [changeBenefit]
  )

  const handleDeleteBenefit = useCallback(
    (key: string) => () => {
      removeBenefit(key)
    },
    [removeBenefit]
  )

  const reachedMaxLimit = benefitsList.length === benefitsLimit

  const navigate = useNavigate()

  const navigateToProductPage = useCallback(() => {
    navigate("/products")
  }, [])

  return (
    <BenefitsContainer>
      <InstructionsContainer>
        <BodyText size="small" weight="semi-bold" color="gray-dark">
          Adicione até 5 benefícios, detalhando seus recursos e vantagens.
        </BodyText>
        <InstructionsListContainer>
          <IconInformativeYellow />
          <InstructionsList>
            <li>
              <Caption size="large" weight="bold" color="alert">
                Certifique-se de destacar os pontos-chave do seu produto para facilitar a compreensão do cliente.
              </Caption>
            </li>
            <li>
              <Caption size="large" weight="bold" color="alert">
                Adicione um benefício por vez (Limite de [{LIMIT_BENEFITS_CHARACTERS}] caracteres por benefício.)
              </Caption>
            </li>
          </InstructionsList>
          <AddBenefitButton type="button" onClick={handleAddNewBenefit} className={clsx({ disabled: reachedMaxLimit })}>
            <IconAddLarge disabled={reachedMaxLimit} />
          </AddBenefitButton>
        </InstructionsListContainer>
      </InstructionsContainer>

      <BenefitListContainer>
        {benefitsList.map((benefit, index) => {
          return (
            <LineContainer key={benefit.key}>
              <ContainerBenefitsInput>
                <Input
                  type="text"
                  name="benefit"
                  className="fullWidth"
                  label=""
                  value={benefit.value}
                  onChange={handleBenefitChange(benefit.key)}
                  placeholder="Digite"
                  autoFocus={index === benefitsList.length - 1}
                  error={errors[benefit.key]}
                  limitCharacters={LIMIT_BENEFITS_CHARACTERS}
                />
              </ContainerBenefitsInput>

              {benefitsList.length > 1 && (
                <IconContainerDelete onClick={handleDeleteBenefit(benefit.key)}>
                  <IconDeleteSmall />
                </IconContainerDelete>
              )}
            </LineContainer>
          )
        })}
      </BenefitListContainer>

      <ButtonContainer>
        <ButtonWithConfirmation
          label="Cancelar"
          className="fill-width secondary"
          actionWithConfirmation={navigateToProductPage}
          actionHeading="Cancelar"
          actionText="Você tem alterações não salvas que serão perdidas ao sair."
        />
        <Button
          onClick={onSubmit}
          loading={isLoading}
          label="Concluir"
          className={clsx("fill-width", { disabled })}
          disabled={disabled || isLoading}
        />
      </ButtonContainer>
    </BenefitsContainer>
  )
}

export default memo(ProductsBenefits)
